/* eslint-disable react/no-unused-prop-types */
import { useMemo, useState, useCallback, useRef } from 'react';
import { object, shape, string } from 'prop-types';
import { assignIn, keyBy } from 'lodash';
import { t } from '@lingui/macro';

import { filterPropTypes, filterPropDefaults } from '../../page-framework/filter/FilterPropTypes';
import { useCurrentFilterStateById } from '../../../stores/FilterStateStore';
import { useEntityService } from '../../../services/EntityService';
import DataTable from '../../data-table/DataTable';
import { Columns } from '../../data-table/DataTableProps';
import { convertLoadParamsToQueryParams } from '../../data-table/Loaders';

export function useEntity(filterId) {
    const [filterState] = useCurrentFilterStateById(filterId);
    return filterState;
}

export function useEntityName(filterId) {
    const placeholder = '...';
    const entity = useEntity(filterId);

    return useMemo(() => entity?.name || placeholder, [entity]);
}

const DEFAULT_COLUMNS = [
    {
        title: 'Name',
        mapping: 'name',
        flex: 1,
        minWidth: 250,
        sortable: true,
    },
];

function EntityListFilter(props) {
    const { state, filterActions, options } = props;
    const [entityId, setEntityId] = useState(state?.uuid);
    const { current: entityCache } = useRef({});
    const entityService = useEntityService();

    const { entityType, showHeaders = false, columns = DEFAULT_COLUMNS, dataSourceParams = {} } = options;

    function handleSelectionChange({ selection: [newEntityId] }) {
        if (!newEntityId) return;
        setEntityId(newEntityId);

        const newEntity = entityCache[newEntityId];
        if (!newEntity) return;
        filterActions.setState(newEntity);
    }

    const loadEntities = useCallback(
        (loaderParams) => {
            const params = {
                ...dataSourceParams,
                ...convertLoadParamsToQueryParams(loaderParams),
            };
            return entityService.getEntities(entityType, params);
        },
        [entityService, entityType, dataSourceParams]
    );

    const loader = useCallback(
        (params) => {
            return loadEntities(params)
                .then((response) => updateEntityCache(response, entityCache))
                .then(convertEntitiesToDatatableRows);
        },
        [loadEntities, entityCache]
    );

    const selectionRef = useRef({
        selection: entityId ? [entityId] : [],
    });

    return (
        <DataTable
            autoFocusSearch
            disableCSVExport
            disableColumnRemover
            visibleRows={5}
            showHeaders={showHeaders}
            shrink={false}
            loader={loader}
            columns={columns}
            selection={selectionRef.current}
            selectionOptions={{
                mode: 'single',
                idMapping: 'uuid',
                force: true,
                allowReselect: true,
                onChange: handleSelectionChange,
            }}
        />
    );
}

function updateEntityCache(response, entityCache) {
    assignIn(entityCache, keyBy(response?.data, 'uuid'));

    return response;
}

function convertEntitiesToDatatableRows(response) {
    return {
        rows: response.data,
        length: response.total,
    };
}

EntityListFilter.propTypes = {
    filterActions: filterPropTypes.filterActions,
    state: shape({
        uuid: string.isRequired,
        name: string.isRequired,
    }),
    options: shape({
        entityType: string.isRequired,
        columns: Columns,
        dataSourceParams: object,
    }).isRequired,
};

EntityListFilter.defaultProps = {
    ...filterPropDefaults,
    autoApply: true,
    state: undefined,
    options: {
        link: {
            tooltipTemplate: t`View details for <%= name %>`,
            pageStateParams: (value, key) => {
                const pattern = /.*-ChartType/;
                return pattern.test(key);
            },
        },
    },
};

export default EntityListFilter;
